// Registry-Kontext je Plattform und Dock-Zustand (Besitzer: geruest).
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import type { LoadedRegistry } from './load';
import type { PlatformKey, Viewer } from './types';

interface RegistryContextValue {
  registry: LoadedRegistry<PlatformKey>;
  viewer: Viewer;
}

const RegistryContext = createContext<RegistryContextValue | null>(null);

export interface DockState {
  /** Schlüssel des offenen Panels oder null. */
  panel: string | null;
  params: Readonly<Record<string, string>>;
  open: (panel: string, params?: Record<string, string>) => void;
  toggle: (panel: string) => void;
  close: () => void;
}

const DockContext = createContext<DockState | null>(null);

export function RegistryProvider(props: {
  registry: LoadedRegistry<PlatformKey>;
  viewer: Viewer;
  children: ReactNode;
}) {
  const { registry, viewer, children } = props;
  const value = useMemo(() => ({ registry, viewer }), [registry, viewer]);
  const [dock, setDock] = useState<{ panel: string | null; params: Record<string, string> }>({
    panel: null,
    params: {},
  });
  const open = useCallback((panel: string, params: Record<string, string> = {}) => {
    setDock({ panel, params });
  }, []);
  const toggle = useCallback((panel: string) => {
    setDock((current) =>
      current.panel === panel ? { panel: null, params: {} } : { panel, params: {} },
    );
  }, []);
  const close = useCallback(() => setDock({ panel: null, params: {} }), []);
  const dockValue = useMemo(() => ({ ...dock, open, toggle, close }), [dock, open, toggle, close]);
  return (
    <RegistryContext.Provider value={value}>
      <DockContext.Provider value={dockValue}>{children}</DockContext.Provider>
    </RegistryContext.Provider>
  );
}

export function useRegistryContext(): RegistryContextValue {
  const value = useContext(RegistryContext);
  if (!value)
    throw new Error('useRegistry* braucht einen <RegistryProvider> (Plattform-Bootstrap).');
  return value;
}

/** Dock-Zustand (Chat-Panel usw.); das Rendern übernimmt die Shell. */
export function useDock(): DockState {
  const value = useContext(DockContext);
  if (!value) throw new Error('useDock braucht einen <RegistryProvider>.');
  return value;
}

export function useViewer(): Viewer {
  return useRegistryContext().viewer;
}
